// features/draft-invoice/draft-invoice.slice.ts
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchDraftInvoicesApi, fetchDraftInvoicesWithCustomParams, deleteDraftInvoicing } from './draft-invoice.service';
import { DraftInvoiceParams, DraftInvoiceItem, DraftInvoiceResponse } from '../../api/draft-invoice/index';
import { toast } from 'react-toastify';

export const fetchDraftInvoicesList = createAsyncThunk(
  'draftInvoice/fetchDraftInvoicesList',
  async (payload: DraftInvoiceParams, { rejectWithValue }) => {
    try {
      const data = await fetchDraftInvoicesApi(payload);
      return data;
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to fetch draft invoices';
      return rejectWithValue(message);
    }
  }
);

export const fetchDraftInvoicesWithParams = createAsyncThunk(
  'draftInvoice/fetchDraftInvoicesWithParams',
  async (payload: Record<string, any>, { rejectWithValue }) => {
    try {
      const data = await fetchDraftInvoicesWithCustomParams(payload);
      return data;
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to fetch draft invoices';
      return rejectWithValue(message);
    }
  }
);

export const deleteDraftInvoice = createAsyncThunk<number, number, { rejectValue: string }>(
  'draftInvoice/deleteDraftInvoice',
  async (preId, { rejectWithValue }) => {
    try {
      await deleteDraftInvoicing(preId);
      return preId;
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to delete draft invoice';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

type Awaited<T> = T extends Promise<infer U> ? U : T;

type DraftInvoiceState = {
  message: string | null;
  invoicesData: DraftInvoiceItem[] | null;
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  filters: {
    type?: number;
    status?: number;
  };
};

const initialState: DraftInvoiceState = {
  message: null,
  invoicesData: null,
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  },
  filters: {
    type: undefined,
    status: undefined,
  },
};

const draftInvoiceSlice = createSlice({
  name: 'draftInvoice',
  initialState,
  reducers: {
    clearInvoicesData: (state) => {
      state.invoicesData = null;
      state.error = null;
      state.message = null;
    },
    setCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
    setPageSize: (state, action) => {
      state.pagination.pageSize = action.payload;
      state.pagination.currentPage = 1;
    },
    setType: (state, action) => {
      state.filters.type = action.payload;
      state.pagination.currentPage = 1;
    },
    setStatus: (state, action) => {
      state.filters.status = action.payload;
      state.pagination.currentPage = 1;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearMessage: (state) => {
      state.message = null;
    },
  },
  extraReducers: builder => {
    // fetchDraftInvoicesList
    builder
      .addCase(fetchDraftInvoicesList.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(fetchDraftInvoicesList.fulfilled, (state, action) => {
        state.loading = false;
        state.invoicesData = action.payload.response;
        state.pagination = {
          currentPage: action.meta.arg.PageNumber,
          pageSize: action.meta.arg.PageSize,
          totalCount: action.payload.totalCount,
          totalPages: Math.ceil(action.payload.totalCount / action.meta.arg.PageSize),
          hasNextPage: action.meta.arg.PageNumber < Math.ceil(action.payload.totalCount / action.meta.arg.PageSize),
          hasPreviousPage: action.meta.arg.PageNumber > 1,
        };
        state.message = action.payload.message;
      })
      .addCase(fetchDraftInvoicesList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.invoicesData = null;
      });
    builder
      .addCase(fetchDraftInvoicesWithParams.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(fetchDraftInvoicesWithParams.fulfilled, (state, action) => {
        state.loading = false;
        state.invoicesData = action.payload.response;
        state.message = action.payload.message;
      })
      .addCase(fetchDraftInvoicesWithParams.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.invoicesData = null;
      });
    builder
      .addCase(deleteDraftInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(deleteDraftInvoice.fulfilled, (state, action) => {
        state.loading = false;
        // remove deleted item from invoicesData if present
        const deletedId = action.payload;
        if (state.invoicesData) {
          state.invoicesData = state.invoicesData.filter(item => item.id !== deletedId);
        }
        state.message = `Draft invoice ${deletedId} deleted`;
      })
      .addCase(deleteDraftInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearInvoicesData,
  setCurrentPage,
  setPageSize,
  setType,
  setStatus,
  clearError,
  clearMessage,
} = draftInvoiceSlice.actions;

export default draftInvoiceSlice.reducer;
